import { useState, useMemo } from 'react';
import { Check, X, ChevronDown, ChevronUp, Filter, Copy, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { VenueResult } from '@/types/dashboard';

interface VenueResultsProps {
  venues: VenueResult[];
  datasetName?: string;
  isMultiRun?: boolean;
}

type FilterTab = 'all' | 'positives' | 'negatives' | 'errors';
type SortField = 'name' | 'score' | 'status';
type SortDir = 'asc' | 'desc';

function StatusIcon({ found }: { found: boolean }) {
  return found ? (
    <Check className="h-4 w-4 text-green-400" />
  ) : (
    <X className="h-4 w-4 text-red-400" />
  );
}

function ScoreBadge({ score }: { score?: number }) {
  if (score === undefined || score === null) {
    return <span className="text-xs text-muted-foreground">--</span>;
  }

  let color = 'text-red-400';
  if (score >= 8) color = 'text-green-400';
  else if (score >= 5) color = 'text-yellow-400';
  else if (score >= 3) color = 'text-orange-400';

  return (
    <span className={cn('text-sm font-bold tabular-nums', color)}>
      {score.toFixed(1)}
    </span>
  );
}

function formatVenuesForCopy(venues: VenueResult[], datasetName?: string): string {
  const lines: string[] = [];
  if (datasetName) lines.push(`## ${datasetName}\n`);

  for (const v of venues) {
    const isError =
      (v.type === 'positive' && !v.in_phase2) || (v.type === 'negative' && v.in_phase2);
    const status = isError ? 'ERROR' : 'OK';
    const score = v.rerank_score != null ? v.rerank_score.toFixed(1) : '--';
    const p1 = v.in_phase1 ? 'yes' : 'no';
    const p2 = v.in_phase2 ? 'yes' : 'no';

    lines.push(`${v.name}`);
    lines.push(`${v.type}  ${status}  score:${score}  p1:${p1}  p2:${p2}`);
    if (v.evidence) lines.push(`evidence: ${v.evidence}`);
    if (v.reason) lines.push(`reason: ${v.reason}`);
    if (v.rerank_reason) lines.push(`rerank: ${v.rerank_reason}`);
    lines.push('');
  }

  return lines.join('\n');
}

function HitRateBadge({ rate }: { rate?: number }) {
  if (rate === undefined || rate === null) return null;
  let color = 'text-red-400 bg-red-500/15';
  if (rate === 100) color = 'text-green-400 bg-green-500/15';
  else if (rate >= 80) color = 'text-green-400/80 bg-green-500/10';
  else if (rate >= 50) color = 'text-yellow-400 bg-yellow-500/15';
  else if (rate > 0) color = 'text-orange-400 bg-orange-500/15';
  return (
    <span className={cn('text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded', color)}>
      {rate}%
    </span>
  );
}

export function VenueResults({ venues, datasetName, isMultiRun }: VenueResultsProps) {
  const [filter, setFilter] = useState<FilterTab>('all');
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedVenue, setExpandedVenue] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const filteredVenues = useMemo(() => {
    let result = [...venues];

    // Apply filter
    switch (filter) {
      case 'positives':
        result = result.filter((v) => v.type === 'positive');
        break;
      case 'negatives':
        result = result.filter((v) => v.type === 'negative');
        break;
      case 'errors':
        result = result.filter((v) => {
          // Errors: positive missed in phase2 OR negative found in phase2
          if (v.type === 'positive' && !v.in_phase2) return true;
          if (v.type === 'negative' && v.in_phase2) return true;
          return false;
        });
        break;
    }

    // Apply sort
    result.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'score':
          cmp = (a.rerank_score ?? -1) - (b.rerank_score ?? -1);
          break;
        case 'status': {
          // Sort errors first
          const aError =
            (a.type === 'positive' && !a.in_phase2) ||
            (a.type === 'negative' && a.in_phase2);
          const bError =
            (b.type === 'positive' && !b.in_phase2) ||
            (b.type === 'negative' && b.in_phase2);
          cmp = (aError ? 1 : 0) - (bError ? 1 : 0);
          break;
        }
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [venues, filter, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filterTabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: venues.length },
    {
      key: 'positives',
      label: 'Positives',
      count: venues.filter((v) => v.type === 'positive').length,
    },
    {
      key: 'negatives',
      label: 'Negatives',
      count: venues.filter((v) => v.type === 'negative').length,
    },
    {
      key: 'errors',
      label: 'Errors Only',
      count: venues.filter((v) => {
        if (v.type === 'positive' && !v.in_phase2) return true;
        if (v.type === 'negative' && v.in_phase2) return true;
        return false;
      }).length,
    },
  ];

  const getRowColor = (venue: VenueResult): string => {
    if (venue.type === 'positive') {
      if (venue.in_phase1 && !venue.in_phase2) {
        // Reranker killed it
        return 'bg-red-500/10 border-l-2 border-l-red-500';
      }
      if (venue.in_phase2) {
        return 'bg-green-500/5 border-l-2 border-l-green-500/50';
      }
      // Missed entirely
      return 'bg-red-500/5 border-l-2 border-l-red-500/50';
    }
    // Negative
    if (venue.in_phase2) {
      // False positive
      return 'bg-red-500/10 border-l-2 border-l-red-500';
    }
    return 'bg-green-500/5 border-l-2 border-l-green-500/30';
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ChevronUp className="h-3 w-3 inline ml-0.5" />
    ) : (
      <ChevronDown className="h-3 w-3 inline ml-0.5" />
    );
  };

  return (
    <div className="rounded-xl border border-primary/20 bg-card/50 p-5 backdrop-blur-xs">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold bg-linear-to-r from-primary to-orange-400 bg-clip-text text-transparent">
          Venue Results
        </h3>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const text = formatVenuesForCopy(filteredVenues, datasetName);
              navigator.clipboard.writeText(text);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border',
              copied
                ? 'bg-green-500/20 border-green-500/30 text-green-400'
                : 'bg-card/50 border-primary/20 text-muted-foreground hover:text-foreground hover:bg-card'
            )}
          >
            {copied ? <CheckCheck className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy Results'}
          </button>
          <div className="flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {filteredVenues.length} of {venues.length}
            </span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1.5 mb-4">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
              filter === tab.key
                ? 'bg-primary text-primary-foreground shadow-xs glow'
                : 'bg-card/50 text-muted-foreground hover:text-foreground hover:bg-card border border-primary/20'
            )}
          >
            {tab.label}
            <span className="ml-1.5 opacity-70">({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-primary/10">
              <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th
                className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('name')}
              >
                Venue <SortIcon field="name" />
              </th>
              <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Type
              </th>
              <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {isMultiRun ? 'P1 Hit%' : 'Phase 1'}
              </th>
              <th className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {isMultiRun ? 'P2 Hit%' : 'Phase 2'}
              </th>
              <th
                className="text-center py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
                onClick={() => handleSort('score')}
              >
                Score <SortIcon field="score" />
              </th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Rerank Reason
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredVenues.map((venue) => {
              const isExpanded = expandedVenue === venue.name;
              const isError =
                (venue.type === 'positive' && !venue.in_phase2) ||
                (venue.type === 'negative' && venue.in_phase2);

              return (
                <tr
                  key={venue.name}
                  className={cn(
                    'transition-colors cursor-pointer',
                    getRowColor(venue),
                    'hover:bg-primary/5'
                  )}
                  onClick={() =>
                    setExpandedVenue(isExpanded ? null : venue.name)
                  }
                >
                  <td className="py-2.5 px-3">
                    {isError ? (
                      <X className="h-4 w-4 text-red-400" />
                    ) : (
                      <Check className="h-4 w-4 text-green-400" />
                    )}
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">
                        {venue.name}
                      </span>
                      {(venue.evidence || venue.reason) && (
                        <button className="text-muted-foreground hover:text-foreground">
                          {isExpanded ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )}
                        </button>
                      )}
                    </div>
                    {isExpanded && (venue.evidence || venue.reason) && (
                      <div className="mt-2 text-xs text-muted-foreground bg-background/30 rounded p-2 border border-primary/10">
                        {venue.evidence && (
                          <div className="mb-1">
                            <span className="font-semibold text-foreground/70">
                              Evidence:{' '}
                            </span>
                            {venue.evidence}
                          </div>
                        )}
                        {venue.reason && (
                          <div>
                            <span className="font-semibold text-foreground/70">
                              Reason:{' '}
                            </span>
                            {venue.reason}
                          </div>
                        )}
                        {venue.rerank_reason && (
                          <div className="mt-1">
                            <span className="font-semibold text-foreground/70">
                              Rerank reason:{' '}
                            </span>
                            {venue.rerank_reason}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider',
                        venue.type === 'positive'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-red-500/20 text-red-400'
                      )}
                    >
                      {venue.type}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    {isMultiRun && venue.hit_rate_phase1 != null ? (
                      <HitRateBadge rate={venue.hit_rate_phase1} />
                    ) : (
                      <StatusIcon found={venue.in_phase1} />
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    {isMultiRun && venue.hit_rate_phase2 != null ? (
                      <HitRateBadge rate={venue.hit_rate_phase2} />
                    ) : (
                      <StatusIcon found={venue.in_phase2} />
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <ScoreBadge score={venue.rerank_score} />
                    {isMultiRun && venue.rerank_score_stddev != null && venue.rerank_score_stddev > 0 && (
                      <span className="text-[9px] text-muted-foreground/50 ml-0.5">&plusmn;{venue.rerank_score_stddev.toFixed(1)}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-left">
                    <span className="text-xs text-muted-foreground truncate block max-w-[200px]">
                      {venue.rerank_reason || '--'}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filteredVenues.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="py-8 text-center text-muted-foreground text-sm"
                >
                  No venues match the current filter
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
